import { useMemo } from "react";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faRotateLeft, faTrash } from "@fortawesome/free-solid-svg-icons";
import type { ReconciliationGridRow } from "./reconciliationGridTypes";
import { countBulkActionTargets } from "./reconciliationBulkFilters";

export interface ReconciliationBulkActionsProps {
    gridRows: ReconciliationGridRow[];
    disabled?: boolean;
    onBulkStageSync: () => void;
    onBulkRevertSync: () => void;
    onBulkSoftDelete: () => void;
    onBulkRestoreDelete: () => void;
}

export function ReconciliationBulkActions({
    gridRows,
    disabled,
    onBulkStageSync,
    onBulkRevertSync,
    onBulkSoftDelete,
    onBulkRestoreDelete,
}: ReconciliationBulkActionsProps) {
    const counts = useMemo(() => countBulkActionTargets(gridRows), [gridRows]);

    const hasAny =
        counts.syncAll > 0 ||
        counts.revertSync > 0 ||
        counts.softDeleteAll > 0 ||
        counts.restoreDelete > 0;

    if (!hasAny) return null;

    return (
        <div className="d-flex flex-wrap gap-2 mb-2">
            {counts.syncAll > 0 && (
                <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={disabled}
                    onClick={onBulkStageSync}
                >
                    Sync all {counts.syncAll}
                    <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
                </Button>
            )}
            {counts.revertSync > 0 && (
                <Button
                    variant="primary"
                    size="sm"
                    disabled={disabled}
                    onClick={onBulkRevertSync}
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                    Revert all {counts.revertSync} syncs
                </Button>
            )}
            {counts.softDeleteAll > 0 && (
                <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={disabled}
                    onClick={onBulkSoftDelete}
                >
                    Delete all {counts.softDeleteAll}
                    <FontAwesomeIcon icon={faTrash} className="ms-1" />
                </Button>
            )}
            {counts.restoreDelete > 0 && (
                <Button
                    variant="danger"
                    size="sm"
                    disabled={disabled}
                    onClick={onBulkRestoreDelete}
                >
                    <FontAwesomeIcon icon={faRotateLeft} className="me-1" />
                    Restore all {counts.restoreDelete}
                </Button>
            )}
        </div>
    );
}
